/**
 * Audit-friendly date/time formatting.
 *
 * Per spec: date = DD/MMM/YY (uppercase month), time = hh:MM AM/PM.
 * Examples: 14/MAY/26 · 03:42 PM
 *
 * The server emits UTC ISO timestamps. For a single-region dealership in
 * India we normalize to Asia/Kolkata (IST, +05:30) so that timestamps look
 * the same to every user regardless of browser timezone.
 */
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN",
                "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const IST_TZ = "Asia/Kolkata";

function pad2(n) { return String(n).padStart(2, "0"); }

function _istParts(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // Intl.DateTimeFormat is the simplest way to project an instant into IST.
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(d).map((p) => [p.type, p.value])
  );
  return {
    day: parts.day, month: parseInt(parts.month, 10), year: parts.year,
    hour: parseInt(parts.hour, 10), minute: parts.minute,
  };
}

export function formatAuditDate(iso) {
  const p = _istParts(iso);
  if (!p) return "";
  return `${p.day}/${MONTHS[p.month - 1]}/${String(p.year).slice(-2)}`;
}

export function formatAuditTime(iso) {
  const p = _istParts(iso);
  if (!p) return "";
  let h = p.hour;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${pad2(h)}:${p.minute} ${ampm}`;
}

export function formatAuditDateTime(iso) {
  const date = formatAuditDate(iso);
  const time = formatAuditTime(iso);
  if (!date) return "";
  return `${date} ${time}`;
}

// Short aliases — preferred everywhere the user sees a date.
// All app surfaces should display DD/MMM/YY (+ hh:MM AM/PM when relevant).
export const fmtDate = formatAuditDate;
export const fmtTime = formatAuditTime;
export const fmtDateTime = formatAuditDateTime;

// ── Numeric short format helpers ────────────────────────────────────────
// Used in dense table rows where vertical space is at a premium (Sales
// listing rows show "25/05/26 · 09:15 AM" under the SR number). All inputs
// are IST-normalised — same as the audit-friendly variants above.
export function fmtShortDate(iso) {
  const p = _istParts(iso);
  if (!p) return "";
  return `${p.day}/${pad2(p.month)}/${String(p.year).slice(-2)}`;
}

export function fmtShortDateTime(iso) {
  const d = fmtShortDate(iso);
  const t = formatAuditTime(iso);
  if (!d) return "";
  return t ? `${d} · ${t}` : d;
}

// Combine a YYYY-MM-DD date string with an optional HH:MM time string into
// the compact "DD/MM/YY · hh:MM AM/PM" form. Skips the time half when not
// supplied so callers don't have to special-case partial data.
export function fmtShortDateAndTime(dateStr, timeStr) {
  if (!dateStr) return "";
  // Build an ISO that the formatters can chew on. Time is optional.
  const iso = `${dateStr}T${(timeStr || "00:00")}:00+05:30`;
  return timeStr ? fmtShortDateTime(iso) : fmtShortDate(iso);
}
